import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, partnerFileUrl, errorText, formatDay, formatDateTime, todayLisbon } from "@/lib/partnerApi";
import { P_CARD, P_H1, P_FIELD, P_LABEL, P_BTN, P_BTN_GHOST, BackLink, StatusPill, PageLoading } from "@/components/partner/PartnerUI";
import { PhotoPicker, photoIds, photosBusy } from "@/components/partner/PhotoPicker";

const asPhotos = (ids) => (ids || []).map((id) => ({ key: id, id, preview: partnerFileUrl(id) }));

export default function PartnerUpdateForm() {
    const { leadId, updateId } = useParams();
    const location = useLocation();
    const [obra, setObra] = useState(null);
    const [existing, setExisting] = useState(updateId ? null : false);
    const [sent, setSent] = useState("");

    useEffect(() => { setSent(""); }, [location.key]);

    useEffect(() => {
        partnerApi.get(`/obras/${leadId}`).then(({ data }) => setObra(data)).catch(() => setObra(false));
        if (updateId) {
            partnerApi.get(`/updates/${updateId}`).then(({ data }) => setExisting(data)).catch(() => setExisting(false));
        }
    }, [leadId, updateId]);

    const back = <BackLink to={`/parceiros/obras/${leadId}`} label="Voltar à obra" />;
    if (obra === null || existing === null) return <PageLoading />;
    if (!obra || (updateId && !existing)) return <>{back}<div className={P_CARD}>Atualização não encontrada.</div></>;
    if (sent) return <SentScreen leadId={leadId} message={sent} />;
    if (existing && !existing.can_edit) return <>{back}<UpdateReadOnly u={existing} /></>;
    return (
        <>
            {back}
            <div>
                <p className="text-sm text-stone-500">{obra.title}</p>
                <h1 className={`${P_H1} mt-1`}>{existing ? "Rever atualização" : "Nova atualização de obra"}</h1>
            </div>
            {existing && <ReviewBanner u={existing} />}
            <UpdateFields key={location.key} leadId={leadId} existing={existing} onSent={setSent} />
        </>
    );
}

function ReviewBanner({ u }) {
    return (
        <div className={`rounded-3xl p-4 sm:p-5 ${u.status === "changes_requested" ? "bg-orange-50 border border-orange-200" : "bg-amber-50 border border-amber-200"}`} data-testid="partner-update-review-banner">
            <StatusPill status={u.status} />
            {u.status === "changes_requested" && u.admin_note && (
                <p className="mt-2 text-[15px] text-orange-950 leading-relaxed whitespace-pre-wrap" data-testid="partner-update-admin-note">
                    <strong>Observação de O Homem da Obra:</strong> {u.admin_note}
                </p>
            )}
            <p className="mt-2 text-sm text-stone-600">Pode corrigir e acrescentar fotografias. Ao reenviar, a atualização volta a ser analisada.</p>
        </div>
    );
}

function UpdateFields({ leadId, existing, onSent }) {
    const today = todayLisbon();
    const [workDate, setWorkDate] = useState(existing?.work_date || today);
    const [works, setWorks] = useState(existing?.works_done || "");
    const [issues, setIssues] = useState(existing?.issues || "");
    const [next, setNext] = useState(existing?.next_works || "");
    const [photos, setPhotos] = useState(asPhotos(existing?.photo_file_ids));
    const [busy, setBusy] = useState(false);
    const uploading = photosBusy(photos);

    const submit = async (e) => {
        e.preventDefault();
        if (!works.trim()) { toast.error("Descreva os trabalhos realizados."); return; }
        setBusy(true);
        const body = { work_date: workDate, works_done: works, issues, next_works: next, photo_file_ids: photoIds(photos) };
        try {
            const { data } = existing
                ? await partnerApi.put(`/updates/${existing.id}`, body)
                : await partnerApi.post(`/obras/${leadId}/updates`, body);
            onSent(data.message);
        } catch (err) {
            toast.error(errorText(err));
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className={`${P_CARD} space-y-6`} data-testid="partner-update-form">
            <label className="block">
                <span className={P_LABEL}>Data dos trabalhos</span>
                <input type="date" value={workDate} max={today} required onChange={(e) => setWorkDate(e.target.value)} className={`${P_FIELD} sm:max-w-[240px]`} data-testid="partner-update-date" />
            </label>
            <label className="block">
                <span className={P_LABEL}>Trabalhos realizados *</span>
                <textarea rows={5} value={works} onChange={(e) => setWorks(e.target.value)} maxLength={5000} placeholder="Ex.: Concluída a montagem da estrutura metálica do teto falso e iniciada a aplicação das placas de gesso cartonado." className={`${P_FIELD} resize-none`} data-testid="partner-update-works" />
            </label>
            <div>
                <span className={P_LABEL}>Fotografias</span>
                <PhotoPicker leadId={leadId} photos={photos} setPhotos={setPhotos} testid="partner-update-photos" />
            </div>
            <label className="block">
                <span className={P_LABEL}>Problemas ou imprevistos <span className="font-normal text-stone-500">(opcional)</span></span>
                <textarea rows={3} value={issues} onChange={(e) => setIssues(e.target.value)} maxLength={3000} placeholder="Ex.: Foi identificada uma tubagem não prevista na zona de intervenção." className={`${P_FIELD} resize-none`} data-testid="partner-update-issues" />
                <span className="block text-sm text-stone-500 mt-1.5">Se for urgente (segurança, danos ou impedimento), ligue já para O Homem da Obra.</span>
            </label>
            <label className="block">
                <span className={P_LABEL}>Próximos trabalhos <span className="font-normal text-stone-500">(opcional)</span></span>
                <textarea rows={3} value={next} onChange={(e) => setNext(e.target.value)} maxLength={3000} placeholder="Ex.: Continuação da aplicação das placas e preparação das juntas." className={`${P_FIELD} resize-none`} data-testid="partner-update-next" />
            </label>
            <button type="submit" disabled={busy || uploading || !works.trim()} className={`${P_BTN} !w-full`} data-testid="partner-update-submit">
                {busy || uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {uploading ? "A enviar fotografias…" : existing ? "Reenviar atualização" : "Enviar atualização"}
            </button>
        </form>
    );
}

function SentScreen({ leadId, message }) {
    const nav = useNavigate();
    return (
        <div className={`${P_CARD} text-center py-10`} data-testid="partner-update-sent">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <p className="mt-5 text-lg text-stone-800 leading-relaxed max-w-md mx-auto" data-testid="partner-update-sent-message">{message}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/parceiros/obras/${leadId}`} className={P_BTN} data-testid="partner-sent-back">Voltar à obra</Link>
                <button type="button" onClick={() => nav(`/parceiros/obras/${leadId}/nova`)} className={P_BTN_GHOST} data-testid="partner-sent-new">Nova atualização</button>
            </div>
        </div>
    );
}

function UpdateReadOnly({ u }) {
    return (
        <div className={`${P_CARD} space-y-5`} data-testid="partner-update-readonly">
            <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl font-bold text-[hsl(var(--forest))] capitalize">{formatDay(u.work_date)}</h1>
                <StatusPill status={u.status} testid="partner-update-readonly-status" />
            </div>
            <p className="text-sm text-stone-500">Enviada em {formatDateTime(u.submitted_at)}</p>
            {u.status === "rejected" && u.reject_reason && (
                <p className="rounded-2xl bg-stone-100 p-4 text-[15px] text-stone-800" data-testid="partner-update-reject-reason"><strong>Motivo:</strong> {u.reject_reason}</p>
            )}
            <Block label="Trabalhos realizados" text={u.works_done} />
            <Block label="Problemas ou imprevistos" text={u.issues} />
            <Block label="Próximos trabalhos" text={u.next_works} />
            {u.photo_file_ids.length > 0 && (
                <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {u.photo_file_ids.map((id) => (
                        <li key={id} className="aspect-square rounded-xl overflow-hidden bg-stone-100"><img src={partnerFileUrl(id)} alt="" className="w-full h-full object-cover" loading="lazy" /></li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Block({ label, text }) {
    if (!text) return null;
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</div>
            <p className="mt-1 text-base text-stone-800 whitespace-pre-wrap leading-relaxed">{text}</p>
        </div>
    );
}
